/**
 * 「よく使われる事例」タブの本文 (事例一覧 + 音声チューニングナレッジ)。
 *
 * ## 構成
 *
 *   1. セクションの説明
 *   2. タグ絞り込み (`UseCaseTagFilter`)
 *   3. 事例一覧 (`UseCaseCard`)
 *   4. 音声チューニングナレッジ (`KnowledgeCard`) + 状態の凡例
 *
 * ## なぜ未解決のナレッジも同じ画面に出すか
 *
 * Readme を **構文辞典ではなくナレッジベース**として育てるため。
 * 「調べたが直らなかった」という記録が Readme の外 (issue やメモ) にあると、
 * 同じ課題を別の人がゼロから調べ直すことになる。
 * よって解決できたものだけでなく、`unsolved` も並べる。
 *
 * ただし対策の無い記録を事例として並べると「使えない事例」に見えるため、
 * **事例とは別枠**にし、各カードへ状態バッジ
 * (`✅ 解決済み` / `🟡 改善方法あり` / `⚪ 未解決`) を出して
 * 「今どこまで分かっているか」を先に伝える。
 * 記号の意味が伝わらないと状態が読めないため、一覧の直前に凡例を置く。
 *
 * ## 設計上の約束
 *
 * - **データは props で受ける** (静的データを import しない)。
 * - 絞り込みの判定は `useCaseFilter.ts` の純関数に任せ、本体は描画に専念する
 *   (将来の文字列検索追加時に本ファイルを触らずに済む)。
 */
import type React from 'react';

import { KnowledgeCard } from './KnowledgeCard';
import { UseCaseCard } from './UseCaseCard';
import { UseCaseTagFilter } from './UseCaseTagFilter';
import { SSML_KNOWLEDGE_STATUSES } from './knowledgeStatus';
import type { SsmlReadmeCategoryKey, SsmlTuningKnowledge, SsmlUseCase } from './types';
import type { SsmlUseCaseTag, SsmlUseCaseTagDefinition } from './useCaseTags';

export interface UseCasePanelProps {
  /** セクションの概要文 */
  description: string;
  /** 絞り込み後の事例一覧 (絞り込み自体は親が `useCaseFilter` で行う) */
  useCases: readonly SsmlUseCase[];
  /** 絞り込み後の音声チューニングナレッジ (解決済み・未解決を問わず並べる) */
  knowledge: readonly SsmlTuningKnowledge[];
  /** タグ絞り込みに出すタグ */
  tags: readonly SsmlUseCaseTagDefinition[];
  /** 選択中のタグ */
  selectedTags: readonly SsmlUseCaseTag[];
  /** タグごとの件数 */
  tagCounts?: ReadonlyMap<SsmlUseCaseTag, number>;
  /** タグのトグル通知 */
  onToggleTag: (tag: SsmlUseCaseTag) => void;
  /** タグ選択解除の通知 */
  onClearTags: () => void;
  /** 関連構文が押されたときの通知 (構文辞典タブへの遷移は親の責務) */
  onSelectRelatedSyntax?: (category: SsmlReadmeCategoryKey) => void;
}

export function UseCasePanel({
  description,
  useCases,
  knowledge,
  tags,
  selectedTags,
  tagCounts,
  onToggleTag,
  onClearTags,
  onSelectRelatedSyntax,
}: UseCasePanelProps): React.JSX.Element {
  return (
    <section
      className="ssml-readme-panel"
      role="tabpanel"
      id="ssml-readme-section-panel-use-cases"
      aria-labelledby="ssml-readme-section-tab-use-cases"
      data-testid="ssml-readme-section-panel-use-cases"
    >
      <h3 className="ssml-readme-panel__title">よく使われる事例</h3>
      <p className="ssml-readme-panel__description">{description}</p>

      <UseCaseTagFilter
        tags={tags}
        selectedTags={selectedTags}
        tagCounts={tagCounts}
        onToggleTag={onToggleTag}
        onClearTags={onClearTags}
      />

      {/*
        タグの組み合わせによっては 0 件になる。
        そのとき「壊れた」と誤解されないよう、解除の手がかりを添えて明示する。
      */}
      {useCases.length === 0 ? (
        <p className="ssml-readme-panel__empty" data-testid="ssml-usecase-empty">
          選択したタグに該当する事例はありません。「すべて表示」で絞り込みを解除できます。
        </p>
      ) : (
        <ul className="ssml-usecase-cards">
          {useCases.map((useCase) => (
            <UseCaseCard
              key={useCase.id}
              useCase={useCase}
              onSelectRelatedSyntax={onSelectRelatedSyntax}
            />
          ))}
        </ul>
      )}

      {/*
        音声チューニングナレッジ。
        0 件のときは枠自体を出さない (空の見出しだけが残ると本文が読みにくい)。
      */}
      {knowledge.length === 0 ? null : (
        <div className="ssml-knowledge" data-testid="ssml-knowledge-list">
          <h4 className="ssml-knowledge__title">音声チューニングナレッジ</h4>
          <p className="ssml-knowledge__lead">
            現場で調べた結果の記録です。<strong>解決できなかったものも残しています</strong>
            (同じ課題を別の人がゼロから調べ直さないようにするため)。
            音声は話者・エンジンの更新で結果が変わるため、各記録の最終確認日を併記しています。
          </p>

          {/*
            状態の凡例。記号だけでは意味が伝わらないため、
            一覧の直前に「記号 = 文言 = 意味」を明示する。
          */}
          <ul className="ssml-knowledge__legend" data-testid="ssml-knowledge-legend">
            {SSML_KNOWLEDGE_STATUSES.map((definition) => (
              <li
                key={definition.status}
                className={`ssml-knowledge__legend-item is-${definition.status}`}
                data-testid={`ssml-knowledge-legend-${definition.status}`}
              >
                <span className="ssml-knowledge__legend-label">
                  {definition.icon} {definition.label}
                </span>
                <span className="ssml-knowledge__legend-description">{definition.description}</span>
              </li>
            ))}
          </ul>

          <ul className="ssml-knowledge__list">
            {knowledge.map((item) => (
              <KnowledgeCard key={item.id} knowledge={item} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
